import type { TravelRoute, AnimatorResult } from './TravelTypes';
import { axialKey } from '../math/HexMath';
import { evalCatmullRom } from '../math/CatmullRomSpline';

const SQRT3 = 1.7320508075688772;

/**
 * Animates movement along a TravelRoute, hop by hop.
 * All scratch variables pre-allocated — zero allocations in update().
 */
export class TravelAnimator {
  private _route: TravelRoute | null = null;
  private _hexRadius: number;

  private _wpX = new Float32Array(256);
  private _wpZ = new Float32Array(256);
  private _wpLen = 0;

  private _hopIdx = 0;
  private _hopElapsed = 0;
  // Catmull-Rom control points (P0, P1=from, P2=to, P3=next)
  private _p0x = 0; private _p0z = 0;
  private _p1x = 0; private _p1z = 0;
  private _p2x = 0; private _p2z = 0;
  private _p3x = 0; private _p3z = 0;
  private _smoothTurn = 0;

  private readonly _result: AnimatorResult = {
    worldX: 0, worldZ: 0, dirX: 0, dirZ: -1, turnRate: 0,
    hexQ: 0, hexR: 0, hopCompleted: false, finished: false,
  };

  constructor(hexRadius: number) {
    this._hexRadius = hexRadius;
  }

  start(route: TravelRoute, planetKeys: ReadonlySet<number> | null): void {
    this._route = route;
    this._hopIdx = 0;
    this._hopElapsed = 0;
    this._smoothTurn = 0; this._result.turnRate = 0;
    this._buildWaypoints(route, planetKeys);
    this._prepareHop(0);
  }

  stop(): void {
    this._route = null;
    this._hopIdx = 0; this._hopElapsed = 0;
    this._smoothTurn = 0; this._result.turnRate = 0;
  }

  get active(): boolean {
    return this._route !== null;
  }

  update(dt: number): AnimatorResult {
    const r = this._result;
    r.hopCompleted = false;
    r.finished = false;

    if (!this._route) {
      r.finished = true;
      return r;
    }

    const route = this._route;
    const hopDuration = route.segmentDurations[this._hopIdx];

    this._hopElapsed += dt;
    if (this._hopElapsed >= hopDuration) {
      this._hopElapsed -= hopDuration;
      r.hopCompleted = true;
      const nextHex = route.hexes[this._hopIdx + 1];
      r.hexQ = nextHex.q; r.hexR = nextHex.r;
      r.worldX = this._p2x; r.worldZ = this._p2z;

      this._hopIdx++;
      if (this._hopIdx >= route.hexes.length - 1) {
        r.finished = true;
        this._route = null;
        return r;
      }
      this._prepareHop(this._hopIdx);
      this._evalSpline(0);
      r.worldX = this._p1x;
      r.worldZ = this._p1z;
      return r;
    }

    this._evalSpline(this._hopElapsed / hopDuration);
    const cur = route.hexes[this._hopIdx];
    r.hexQ = cur.q; r.hexR = cur.r;
    return r;
  }

  private _evalSpline(t: number): void {
    this._smoothTurn = evalCatmullRom(
      t, this._p0x, this._p0z, this._p1x, this._p1z,
      this._p2x, this._p2z, this._p3x, this._p3z,
      this._result, this._smoothTurn,
    );
  }

  private _hexToX(q: number): number { return this._hexRadius * 1.5 * q; }
  private _hexToZ(q: number, r: number): number { return this._hexRadius * SQRT3 * (r + q * 0.5); }

  private _buildWaypoints(route: TravelRoute, planetKeys: ReadonlySet<number> | null): void {
    const hexes = route.hexes;
    const n = hexes.length;
    if (n > this._wpX.length) {
      this._wpX = new Float32Array(n);
      this._wpZ = new Float32Array(n);
    }
    this._wpLen = n;

    // Pass 1: raw hex centers
    for (let i = 0; i < n; i++) {
      this._wpX[i] = this._hexToX(hexes[i].q);
      this._wpZ[i] = this._hexToZ(hexes[i].q, hexes[i].r);
    }

    // Pass 2: offset planet waypoints perpendicular to travel direction
    if (!planetKeys || planetKeys.size === 0) return;
    const offset = this._hexRadius * 1.1;
    for (let i = 0; i < n; i++) {
      if (!planetKeys.has(axialKey(hexes[i].q, hexes[i].r))) continue;
      const pi = i > 0 ? i - 1 : 0;
      const ni = i < n - 1 ? i + 1 : n - 1;
      const dx = this._wpX[ni] - this._wpX[pi], dz = this._wpZ[ni] - this._wpZ[pi];
      const len = Math.sqrt(dx * dx + dz * dz);
      if (len < 1e-6) continue;
      const perpX = dz / len, perpZ = -dx / len;
      this._wpX[i] += perpX * offset;
      this._wpZ[i] += perpZ * offset;
    }
  }

  private _prepareHop(idx: number): void {
    const last = this._wpLen - 1;

    this._p1x = this._wpX[idx]; this._p1z = this._wpZ[idx];
    this._p2x = this._wpX[idx + 1]; this._p2z = this._wpZ[idx + 1];

    if (idx > 0) {
      this._p0x = this._wpX[idx - 1]; this._p0z = this._wpZ[idx - 1];
    } else {
      this._p0x = 2 * this._p1x - this._p2x;
      this._p0z = 2 * this._p1z - this._p2z;
    }

    if (idx + 2 <= last) {
      this._p3x = this._wpX[idx + 2]; this._p3z = this._wpZ[idx + 2];
    } else {
      this._p3x = 2 * this._p2x - this._p1x;
      this._p3z = 2 * this._p2z - this._p1z;
    }

    const dx = 0.5 * (-this._p0x + this._p2x);
    const dz = 0.5 * (-this._p0z + this._p2z);
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len > 1e-6) { this._result.dirX = dx / len; this._result.dirZ = dz / len; }
  }

}
